// World expansion extras for WORLD COSTA BLANCA (mission 2026-08-19:
// "Visual Quality + World Expansion Loop", benchmark: sakura-crossing
// density/charm, identity: Mediterranean Sorolla luminism — NOT Japanese).
//
// Scope: Altea identity silhouette (blue-dome church), street/plaza props,
// golf + marina expansion, ambient world life (people, gulls, plane).
// Everything decorative registers colliders only where the walker can reach
// it; nothing here is a semantic entity (no interactables, no anchors).
// All randomness flows through the seeded rng (QA determinism, INC-002).

import * as THREE from 'three';
import { cel, flat } from './core/toon.js';

// Local palette — hexes match scene-builders C/M so the cel cache shares
// programs and the world stays on one narrow Mediterranean palette.
const P = {
  plaster: 0xf7f4ec,
  plasterWarm: 0xe8dfd0,
  stone: 0xc9bca8,
  cobble: 0xbab3a8,
  ink: 0x20352f,
  blue: 0x326f91,
  blueDark: 0x214e68,
  domeBlue: 0x2e6f9e,
  terracotta: 0xa6654b,
  green: 0x5e7b63,
  greenDark: 0x365844,
  lawn: 0x7d9b6a,
  sand: 0xd7c7a8,
  water: 0x4fa8b8,
  white: 0xffffff,
  clothBlue: 0x7fa8c9,
  clothTerracotta: 0xc98a6b,
  skin: 0xe8c9a8,
  shirtA: 0xc96f4a,
  shirtB: 0x4a7a96,
  shirtC: 0xd9d2c0
};

function box(w, h, d, material, x = 0, y = 0, z = 0) {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), material);
  mesh.position.set(x, y, z);
  mesh.castShadow = mesh.receiveShadow = true;
  return mesh;
}

function addCollider(ctx, box, bottom, top) {
  ctx?.colliders.push({ ...box, bottom, top });
}

// ---------------------------------------------------------------- B1: church
// Altea's unmistakable silhouette: whitewashed parish church with a blue
// glazed-tile dome (Nuestra Señora del Consuelo read) crowning the old town.
function addChurch(root, ctx, x, y, z) {
  const g = new THREE.Group();
  const plaster = cel({ color: P.plaster, bands: 'soft3' });
  const dome = cel({ color: P.domeBlue, bands: 4 });
  const trim = cel({ color: P.blueDark });

  // nave + lower side aisle
  g.add(box(2.6, 2.4, 3.6, plaster, 0, 1.2, 0));
  g.add(box(1.1, 1.5, 3.2, plaster, 1.75, .75, 0));
  // parapet
  g.add(box(2.75, .16, 3.75, cel({ color: P.plasterWarm, bands: 'soft3' }), 0, 2.48, 0));
  // drum + blue glazed dome + lantern (the silhouette)
  const drum = new THREE.Mesh(new THREE.CylinderGeometry(.82, .88, .55, 12), plaster);
  drum.position.set(0, 2.83, -.4);
  g.add(drum);
  const cupola = new THREE.Mesh(new THREE.SphereGeometry(.92, 14, 10, 0, Math.PI * 2, 0, Math.PI / 2), dome);
  cupola.scale.y = 1.15;
  cupola.position.set(0, 3.1, -.4);
  cupola.castShadow = true;
  g.add(cupola);
  const lantern = new THREE.Mesh(new THREE.CylinderGeometry(.16, .2, .42, 8), plaster);
  lantern.position.set(0, 4.2, -.4);
  g.add(lantern);
  const cap = new THREE.Mesh(new THREE.ConeGeometry(.24, .3, 8), dome);
  cap.position.set(0, 4.55, -.4);
  g.add(cap);
  // espadaña (bell wall) over the entrance
  g.add(box(1.15, 1.5, .3, plaster, 0, 3.15, 1.75));
  const bellArch = new THREE.Mesh(new THREE.CylinderGeometry(.22, .22, .34, 10, 1, false, 0, Math.PI), trim);
  bellArch.rotation.x = Math.PI / 2; bellArch.rotation.z = Math.PI / 2;
  bellArch.position.set(0, 3.55, 1.75);
  g.add(bellArch);
  g.add(box(.1, .3, .1, trim, 0, 3.42, 1.78)); // bell hint
  const cross = box(.05, .5, .05, trim, 0, 4.15, 1.75);
  g.add(cross);
  g.add(box(.28, .05, .05, trim, 0, 4.22, 1.75));
  // arched main door
  g.add(box(.6, 1.05, .08, trim, 0, .53, 1.83));
  const doorArch = new THREE.Mesh(new THREE.CylinderGeometry(.3, .3, .08, 12, 1, false, 0, Math.PI), trim);
  doorArch.rotation.x = Math.PI / 2; doorArch.rotation.z = Math.PI / 2;
  doorArch.position.set(0, 1.05, 1.83);
  g.add(doorArch);

  g.position.set(x, y, z);
  g.rotation.y = .55; // facade toward the mirador/plaza approach
  root.add(g);
  addCollider(ctx, { x0: x - 1.6, x1: x + 2.5, z0: z - 2.0, z1: z + 2.0 }, y, y + 4.7);
  return g;
}

// ------------------------------------------------------- B2: plaza + streets
function addFountain(root, ctx, x, y, z) {
  const g = new THREE.Group();
  const stone = cel({ color: P.stone });
  const basin = new THREE.Mesh(new THREE.CylinderGeometry(.85, .95, .34, 14), stone);
  basin.position.y = .17;
  basin.castShadow = basin.receiveShadow = true;
  g.add(basin);
  const water = new THREE.Mesh(
    new THREE.CylinderGeometry(.74, .74, .05, 14),
    cel({ color: P.water, bands: 'soft' })
  );
  water.position.y = .33;
  g.add(water);
  g.add(box(.22, .8, .22, stone, 0, .55, 0));
  const top = new THREE.Mesh(new THREE.CylinderGeometry(.34, .26, .14, 10), stone);
  top.position.y = 1.0;
  g.add(top);
  g.position.set(x, y, z);
  root.add(g);
  addCollider(ctx, { x0: x - .95, x1: x + .95, z0: z - .95, z1: z + .95 }, y, y + 1.1);
}

function addStreetLamp(root, ctx, x, y, z) {
  const iron = cel({ color: P.ink });
  const g = new THREE.Group();
  g.add(box(.07, 2.3, .07, iron, 0, 1.15, 0));
  g.add(box(.3, .05, .3, iron, 0, 2.32, 0));
  g.add(box(.2, .26, .2, cel({ color: P.plasterWarm, emissive: 0xffe6b8, emissiveIntensity: .25 }), 0, 2.16, 0));
  g.add(box(.26, .05, .26, iron, 0, 2.0, 0));
  g.position.set(x, y, z);
  root.add(g);
  addCollider(ctx, { x0: x - .12, x1: x + .12, z0: z - .12, z1: z + .12 }, y, y + 2.35);
}

function addBench(root, ctx, x, y, z, yaw = 0) {
  const g = new THREE.Group();
  const wood = cel({ color: P.terracotta });
  const iron = cel({ color: P.ink });
  g.add(box(1.3, .07, .4, wood, 0, .42, 0));
  g.add(box(1.3, .34, .06, wood, 0, .62, -.2));
  g.add(box(.06, .42, .36, iron, -.55, .21, 0));
  g.add(box(.06, .42, .36, iron, .55, .21, 0));
  g.position.set(x, y, z);
  g.rotation.y = yaw;
  root.add(g);
  addCollider(ctx, { x0: x - .7, x1: x + .7, z0: z - .3, z1: z + .3 }, y, y + .8);
}

// Market stall with striped awning — plaza life, terracotta/white stripes.
function addMarketStall(root, ctx, x, y, z, yaw, rng) {
  const g = new THREE.Group();
  const wood = cel({ color: 0x8a6f52 });
  g.add(box(1.5, .08, .8, wood, 0, .78, 0)); // counter
  g.add(box(.08, .78, .7, wood, -.65, .39, 0));
  g.add(box(.08, .78, .7, wood, .65, .39, 0));
  for (const px of [-.7, .7]) for (const pz of [-.35, .35]) g.add(box(.05, 1.9, .05, wood, px, .95, pz));
  // striped awning
  const stripeA = cel({ color: P.terracotta });
  const stripeB = cel({ color: P.plasterWarm, bands: 'soft3' });
  for (let i = 0; i < 6; i += 1) {
    const s = box(.27, .04, 1.05, i % 2 ? stripeA : stripeB, -.675 + i * .27, 1.95, 0);
    s.rotation.x = -.12;
    g.add(s);
  }
  // goods: ceramics / fruit piles
  const goods = [cel({ color: P.domeBlue }), cel({ color: 0xd9903f }), cel({ color: P.green })];
  for (let i = 0; i < 4; i += 1) {
    const good = new THREE.Mesh(new THREE.SphereGeometry(.12, 8, 6), goods[Math.floor(rng() * 3)]);
    good.position.set(-.55 + i * .36, .9, (rng() - .5) * .4);
    good.scale.y = .75;
    g.add(good);
  }
  g.position.set(x, y, z);
  g.rotation.y = yaw;
  root.add(g);
  addCollider(ctx, { x0: x - .85, x1: x + .85, z0: z - .55, z1: z + .55 }, y, y + 2.0);
}

// Laundry line between facades — old-town micro-DNA.
function addLaundryLine(root, x0, y0, z0, x1, y1, z1, rng) {
  const g = new THREE.Group();
  const from = new THREE.Vector3(x0, y0, z0);
  const to = new THREE.Vector3(x1, y1, z1);
  const len = from.distanceTo(to);
  const line = new THREE.Mesh(new THREE.CylinderGeometry(.012, .012, len, 4), cel({ color: P.ink }));
  line.position.copy(from).lerp(to, .5);
  line.lookAt(to);
  line.rotateX(Math.PI / 2);
  g.add(line);
  const cloths = [P.white, P.clothBlue, P.clothTerracotta, P.plasterWarm];
  const n = 3;
  for (let i = 1; i <= n; i += 1) {
    const p = from.clone().lerp(to, i / (n + 1));
    const cloth = box(.34, .42, .02, cel({ color: cloths[Math.floor(rng() * cloths.length)], bands: 'soft3' }), p.x, p.y - .23, p.z);
    cloth.rotation.y = rng() * .3;
    g.add(cloth);
  }
  root.add(g);
}

// Chiringuito on the shore — beach bar with awning, Mediterranean leisure cue.
function addChiringuito(root, ctx, x, y, z, yaw) {
  const g = new THREE.Group();
  const wood = cel({ color: 0x9a7d5a });
  for (const px of [-1.1, 1.1]) for (const pz of [-.8, .8]) g.add(box(.09, 1.9, .09, wood, px, .95, pz));
  const roof = box(2.7, .07, 2.1, cel({ color: P.plasterWarm, bands: 'soft3' }), 0, 1.95, 0);
  roof.rotation.z = .04;
  g.add(roof);
  g.add(box(2.0, .09, .6, wood, 0, .85, .65)); // bar counter
  g.add(box(2.0, .5, .08, cel({ color: P.domeBlue }), 0, .55, .68)); // blue front panel
  for (let i = 0; i < 3; i += 1) { // stools
    const stool = new THREE.Mesh(new THREE.CylinderGeometry(.14, .16, .5, 8), wood);
    stool.position.set(-.6 + i * .6, .25, 1.15);
    g.add(stool);
  }
  g.position.set(x, y, z);
  g.rotation.y = yaw;
  root.add(g);
  addCollider(ctx, { x0: x - 1.2, x1: x + 1.2, z0: z - .9, z1: z + .9 }, y, y + 2.0);
}

// ------------------------------------------------------- B3: expansion + life
// Pitch-and-putt golf corner on the hilltop lawn: green, flag, bunker.
function addGolfCorner(root, x, y, z) {
  const green = new THREE.Mesh(new THREE.CylinderGeometry(1.6, 1.7, .07, 16), cel({ color: P.lawn, bands: 'soft' }));
  green.position.set(x, y + .02, z);
  green.receiveShadow = true;
  root.add(green);
  const bunker = new THREE.Mesh(new THREE.CylinderGeometry(.7, .8, .06, 12), cel({ color: P.sand, bands: 'soft3' }));
  bunker.scale.z = .6;
  bunker.position.set(x + 1.9, y + .02, z + .8);
  root.add(bunker);
  root.add(box(.035, 1.1, .035, cel({ color: P.white, bands: 'soft3' }), x, y + .58, z));
  const flag = box(.4, .24, .02, cel({ color: P.shirtA }), x + .2, y + 1.0, z);
  root.add(flag);
}

// Lighthouse at the pier end — west-edge focal point, white + altea blue.
function addLighthouse(root, ctx, x, y, z) {
  const g = new THREE.Group();
  const base = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.2, .5, 10), cel({ color: P.stone }));
  base.position.y = .25;
  g.add(base);
  const tower = new THREE.Mesh(new THREE.CylinderGeometry(.42, .58, 2.6, 12), cel({ color: P.plaster, bands: 'soft3' }));
  tower.position.y = 1.8;
  tower.castShadow = true;
  g.add(tower);
  const band = new THREE.Mesh(new THREE.CylinderGeometry(.47, .5, .3, 12), cel({ color: P.domeBlue }));
  band.position.y = 2.2;
  g.add(band);
  const lantern = new THREE.Mesh(new THREE.CylinderGeometry(.34, .34, .4, 10), cel({ color: P.ink }));
  lantern.position.y = 3.3;
  g.add(lantern);
  const cap = new THREE.Mesh(new THREE.ConeGeometry(.4, .35, 10), cel({ color: P.domeBlue }));
  cap.position.y = 3.68;
  g.add(cap);
  g.position.set(x, y, z);
  root.add(g);
  addCollider(ctx, { x0: x - 1.2, x1: x + 1.2, z0: z - 1.2, z1: z + 1.2 }, y, y + 3.8);
}

// Simple strolling figures — world life without semantic entities.
function addFigure(root, x, y, z, yaw, shirt, scale = 1) {
  const g = new THREE.Group();
  const body = new THREE.Mesh(new THREE.CapsuleGeometry(.16 * scale, .5 * scale, 4, 8), cel({ color: shirt }));
  body.position.y = .5 * scale;
  body.castShadow = true;
  const head = new THREE.Mesh(new THREE.SphereGeometry(.14 * scale, 10, 8), cel({ color: P.skin, bands: 'soft3' }));
  head.position.y = .95 * scale;
  g.add(body, head);
  g.position.set(x, y, z);
  g.rotation.y = yaw;
  root.add(g);
}

// Gulls over the sea — two-quad chevrons, gentle bob animated in main.js.
function addGull(root, x, y, z, phase) {
  const g = new THREE.Group();
  const wingMat = cel({ color: 0x4a5560 });
  for (const side of [-1, 1]) {
    const wing = box(.55, .02, .14, wingMat, side * .26, 0, 0);
    wing.rotation.z = side * .38;
    g.add(wing);
  }
  g.position.set(x, y, z);
  g.userData.ambient = 'gull';
  g.userData.baseY = y;
  g.userData.phase = phase;
  root.add(g);
}

// Occasional small plane with contrail crossing the sky (animated in main.js).
function addPlane(root) {
  const g = new THREE.Group();
  const bodyMat = cel({ color: P.white, bands: 'soft3' });
  g.add(box(1.6, .22, .22, bodyMat, 0, 0, 0));
  g.add(box(.5, .04, 1.5, bodyMat, -.1, 0, 0)); // wings
  g.add(box(.3, .3, .04, bodyMat, .75, .12, 0)); // tail
  const trail = new THREE.Mesh(
    new THREE.PlaneGeometry(9, .16),
    flat({ color: 0xffffff, transparent: true, opacity: .4, depthWrite: false, fog: false })
  );
  trail.position.set(5.6, .05, 0);
  g.add(trail);
  g.position.set(-90, 46, -55);
  g.rotation.y = Math.PI; // travels -x -> +x after flip
  g.userData.ambient = 'plane';
  root.add(g);
}

// ------------------------------------------------------------------ entry
export function addWorldExtras(root, ctx) {
  const rng = ctx.rng;

  // B1 — identity silhouette on the hill top, west of the apartment ring so
  // it reads from overview/coast/mirador instead of hiding behind buildings
  addChurch(root, ctx, 1.5, 4.5, -6.5);

  // B2 — plaza + promenade + lane props (seq-local coords use the old-town
  // group transform: origin (-7,.3,-1), yaw -.08 — replicated here so props
  // land on platforms without re-opening scene-builders)
  const SY = -.08, SP = [-7, -1];
  const toWorld = (lx, lz) => [SP[0] + lx * Math.cos(SY) + lz * Math.sin(SY), SP[1] - lx * Math.sin(SY) + lz * Math.cos(SY)];

  const [fx, fz] = toWorld(6.6, -1.3);
  addFountain(root, ctx, fx, .3 + 2.55 + .05, fz);
  const [s1x, s1z] = toWorld(6.9, -3.6);
  addMarketStall(root, ctx, s1x, .3 + 2.55 + .05, s1z, .3, rng);
  const [l1x, l1z] = toWorld(-2.2, 4.4);
  const [l2x, l2z] = toWorld(.3, 4.7);
  addLaundryLine(root, l1x, .3 + 2.1, l1z, l2x, .3 + 2.15, l2z, rng);

  // promenade: lamps + benches alternating with the palms
  for (const lx of [-12.2, -4.8, 2.6, 11.4]) addStreetLamp(root, ctx, lx, .21, 10.6);
  addBench(root, ctx, -8.5, .21, 10.5, Math.PI);
  addBench(root, ctx, 5.5, .21, 10.55, Math.PI);

  // shore: chiringuito west of the sea-villa plot
  addChiringuito(root, ctx, .5, .03, 13.5, -.06);

  // B3 — expansion + life
  addGolfCorner(root, 10.6, 4.58, -6.9);
  addLighthouse(root, ctx, -20.4, 0, 18.4);

  addFigure(root, -1.6, .21, 9.55, 1.4, P.shirtA);
  addFigure(root, -1.1, .21, 9.85, -1.6, P.shirtC, .92);
  addFigure(root, 6.5, .21, 9.9, .9, P.shirtB);
  const [pfx, pfz] = toWorld(7.8, -1.2);
  addFigure(root, pfx, .3 + 2.6, pfz, 2.6, P.shirtB, .95);
  addFigure(root, -14.5, .21, 17.5, 2.9, P.shirtA, .9); // pier stroller
  addFigure(root, 1.6, .03, 12.6, -.7, P.shirtC, .88);  // beach walker

  for (let i = 0; i < 5; i += 1) {
    addGull(root, -16 + rng() * 16, 7.5 + rng() * 4.5, 20 + rng() * 9, rng() * Math.PI * 2);
  }
  addPlane(root);
}
